import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  UsersIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  CogIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ArrowLeftOnRectangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';

const navigation: Array<{
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: 'superadmin' | 'admin';
}> = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Usuários', href: '/users', icon: UsersIcon },
  { name: 'Empresas', href: '/companies', icon: BuildingOfficeIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Configurações', href: '/settings', icon: CogIcon },
  { name: 'Logs', href: '/logs', icon: DocumentTextIcon },
  { name: 'Segurança', href: '/security', icon: ShieldCheckIcon },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const location = useLocation();
  const { user, signOut } = useAuthStore();

  const handleSignOut = async () => {
    await signOut();
  };

  const handleLinkClick = () => {
    // Fechar menu mobile ao clicar em um link
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Sidebar para desktop - sempre visível em telas grandes */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:z-30">
        <div 
          className="flex h-full w-64 flex-col border-r"
          style={{
            backgroundColor: 'var(--sidebar-bg)',
            borderColor: 'var(--border-primary)'
          }}
        >
          {/* Logo */}
          <div 
            className="flex min-h-[64px] shrink-0 items-center px-3 border-b py-2.5"
            style={{ borderColor: 'var(--border-primary)' }}
          >
            <div className="flex items-center space-x-2.5 min-w-0 w-full">
              <div className="w-9 h-9 bg-linkedin-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base">I</span>
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <h1 
                  className="text-sm font-bold leading-tight whitespace-nowrap"
                  style={{ color: 'var(--text-primary)' }}
                >
                  InRock Admin
                </h1>
                <p 
                  className="text-[10px] leading-tight whitespace-nowrap"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Painel Administrativo
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col px-3 sm:px-4 py-4 sm:py-6 space-y-1 sm:space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              // Verificar se o usuário tem permissão para ver este item
              const canAccess = !item.requiredRole || user?.role === item.requiredRole;
              
              if (!canAccess) return null;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                    isActive
                      ? 'bg-linkedin-50 border-r-2 border-linkedin-600'
                      : 'hover:bg-gray-50'
                  )}
                  style={{
                    color: isActive ? '#0284c7' : 'var(--text-primary)',
                    backgroundColor: isActive ? 'rgba(2, 132, 199, 0.1)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="mr-2 sm:mr-3 flex-shrink-0" style={{ color: isActive ? '#0284c7' : 'var(--text-secondary)' }}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t p-3 sm:p-4" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                  {user?.email}
                </p>
                <p className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-1 transition-colors duration-200 flex-shrink-0"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-muted)';
                }}
                title="Sair"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Sidebar para mobile - slide in/out */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-64 border-r transform transition-transform duration-300 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{
          backgroundColor: 'var(--sidebar-bg)',
          borderColor: 'var(--border-primary)'
        }}
      >
        <div className="flex h-full flex-col">
          {/* Header com botão fechar */}
          <div className="flex min-h-[64px] shrink-0 items-center justify-between px-3 border-b py-2.5" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center space-x-2.5 min-w-0 flex-1">
              <div className="w-9 h-9 bg-linkedin-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base">I</span>
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <h1 className="text-sm font-bold leading-tight whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>InRock Admin</h1>
                <p className="text-[10px] leading-tight whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>Painel Administrativo</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 lg:hidden flex-shrink-0 ml-2 transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col px-3 sm:px-4 py-4 sm:py-6 space-y-1 sm:space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              // Verificar se o usuário tem permissão para ver este item
              const canAccess = !item.requiredRole || user?.role === item.requiredRole;
              
              if (!canAccess) return null;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleLinkClick}
                  className={clsx(
                    'group flex items-center px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                    isActive
                      ? 'bg-linkedin-50 border-r-2 border-linkedin-600'
                      : 'hover:bg-gray-50'
                  )}
                  style={{
                    color: isActive ? '#0284c7' : 'var(--text-primary)',
                    backgroundColor: isActive ? 'rgba(2, 132, 199, 0.1)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="mr-2 sm:mr-3 flex-shrink-0" style={{ color: isActive ? '#0284c7' : 'var(--text-secondary)' }}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t p-3 sm:p-4" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                  {user?.email}
                </p>
                <p className="text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-1 transition-colors duration-200 flex-shrink-0"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-muted)';
                }}
                title="Sair"
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
